import { unwrap } from "@sagot/api-client";
import { useMutation, useQueries, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { saveBlob } from "../../engage/shared/download";
import { reportsOf, type KpiId } from "./report-kpis";
import { reportQuery, type ReportSpec } from "./report-query";
import type { ReportRange } from "./report-range";
import type { ReportName, ReportOut, StatsDimension } from "./report-types";

export type ReportResult = UseQueryResult<ReportOut>;
type Period = Pick<ReportRange, "from" | "to" | "compare">;

interface ReportOptions {
  dimension?: StatsDimension;
  /** Defaults to the page's compare switch; the previous-period series is read without it. */
  compare?: boolean;
  enabled?: boolean;
}

const specOf = (report: ReportName, range: Period, options: ReportOptions = {}): ReportSpec => ({
  report,
  dimension: options.dimension ?? "overall",
  from: range.from,
  to: range.to,
  compare: options.compare ?? range.compare,
});

/** One report of the period (`GET /v1/service-reports/{report}`): totals, breakdown rows and the daily series. */
export function useReport(report: ReportName, range: Period, options: ReportOptions = {}): ReportResult {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({ ...reportQuery(api, ws, specOf(report, range, options)), enabled: options.enabled });
}

/** The reports behind a set of headline tiles, by report name (each read once, even when two tiles share it). */
export function useKpiReports(ids: readonly KpiId[], range: Period): Partial<Record<ReportName, ReportResult>> {
  const api = useApi();
  const ws = useWorkspaceId();
  const names = reportsOf(ids);
  const results = useQueries({ queries: names.map((report) => reportQuery(api, ws, specOf(report, range))) });
  return Object.fromEntries(names.map((name, index) => [name, results[index]]));
}

export interface ChartExport {
  report: ReportName;
  dimension: StatsDimension;
}

/**
 * The CSV of one chart's report (`GET /v1/service-reports/{report}/export`, needs `analytics.export`): saved under the name the server
 * gives it. A cross-origin browser may not read `Content-Disposition`; the name is then the server's own pattern, built here.
 */
export function useExportReport(range: Period) {
  const api = useApi();
  return useMutation({
    mutationFn: async ({ report, dimension }: ChartExport) => {
      const result = await api.GET("/v1/service-reports/{report}/export", { params: { path: { report }, query: { from: range.from, to: range.to, dimension } }, parseAs: "blob" });
      const blob = await unwrap(Promise.resolve(result));
      const disposition = result.response.headers.get("Content-Disposition") ?? "";
      const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? `service-${report}-${range.from}-${range.to}.csv`;
      saveBlob(blob, filename);
      return filename;
    },
  });
}

/** Reads the reports behind `ids` on demand (from the cache when fresh), for the summary export. */
export function useSummaryReader(range: Period) {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return async (ids: readonly KpiId[]): Promise<Partial<Record<ReportName, ReportOut>>> => {
    const names = reportsOf(ids);
    const loaded = await Promise.all(names.map((report) => client.query(reportQuery(api, ws, specOf(report, range)))));
    return Object.fromEntries(names.map((name, index) => [name, loaded[index]]));
  };
}
